import * as React from 'react';
import { useSelector } from 'react-redux';
import Form, { FormInstance } from 'antd/lib/form';
import Select from 'antd/lib/select';
import Tag from 'antd/lib/tag';
import { FixRequestFormFieldValue, ReleaseStatus } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppHome, selectFixRequestFormFieldsSettings, selectReleasesForAppId } from '@/selectors/selectors';
import { runwayColors, rwColors, tagStyle } from '@/components/styles/globalStyles';
import { getFixRequestFormFieldId, runeCount } from '@/helpers';
import MarkdownEditor from '@/components/HOCs/MarkdownEditor';

export interface FixRequestFormValues {
    version?: string;
    [fieldName: string]: string | undefined;
}

const releaseTag = (version?: string, liveVersion?: string, nextVersion?: string): string | undefined => {
    if (version && version === nextVersion) {
        return 'NEXT RELEASE';
    }
    if (version && version === liveVersion) {
        return 'LIVE RELEASE';
    }
    return undefined;
};

const VersionLabel: React.FC<{ label: React.ReactNode; tag?: string }> = ({ label, tag }) => {
    const tagColor = tag === 'LIVE RELEASE' ? rwColors.statusGreen : rwColors.statusYellow;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
            <span>{label}</span>
            {tag && (
                <Tag
                    style={{
                        ...tagStyle,
                        color: tagColor,
                        borderColor: tagColor,
                        marginInlineEnd: 0,
                        backgroundColor: runwayColors.white,
                    }}
                >
                    {tag}
                </Tag>
            )}
        </span>
    );
};

export const useFixRequestForm = (appId: string) => {
    const formFieldsSettings = useSelector(selectFixRequestFormFieldsSettings(appId));
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, appId));
    const appHome = useSelector((state: RootState) => selectAppHome(state, appId));
    const liveVersion = appHome?.liveRelease?.version;
    const nextVersion = appHome?.nextRelease?.version;

    const fields = (
        <>
            <Form.Item
                name="version"
                label="Target version"
                layout="horizontal"
                labelAlign="left"
                labelCol={{ flex: '130px' }}
                wrapperCol={{ flex: 'auto' }}
                colon={false}
                initialValue={nextVersion}
                rules={[{ required: true, message: 'Select a release' }]}
            >
                <Select
                    placeholder="Select a release"
                    showSearch
                    optionFilterProp="label"
                    options={releases
                        .filter(
                            (release) =>
                                ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(
                                    release.status,
                                ),
                        )
                        .map((release) => ({
                            value: release.version,
                            label: release.displayableVersion || release.version,
                        }))}
                    optionRender={(option) => (
                        <VersionLabel
                            label={option.label}
                            tag={releaseTag(option.value as string, liveVersion, nextVersion)}
                        />
                    )}
                    labelRender={(props) => (
                        <VersionLabel
                            label={props.label}
                            tag={releaseTag(props.value as string, liveVersion, nextVersion)}
                        />
                    )}
                />
            </Form.Item>
            {formFieldsSettings.map((field) => (
                <Form.Item
                    key={getFixRequestFormFieldId(field)}
                    name={`customField_${getFixRequestFormFieldId(field)}`}
                    label={field.name}
                    initialValue={field.prePopulatedText || undefined}
                    rules={[
                        { required: field.required, message: `${field.name} is required` },
                        {
                            validator: (_, value) => {
                                if (
                                    field.minCharacters &&
                                    field.minCharacters > 0 &&
                                    value &&
                                    runeCount(value) < field.minCharacters
                                ) {
                                    return Promise.reject(
                                        new Error(
                                            `${field.name} is too short (${runeCount(value)}/${field.minCharacters})`,
                                        ),
                                    );
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <MarkdownEditor preview="edit" textareaProps={{ placeholder: field.placeholder || undefined }} />
                </Form.Item>
            ))}
        </>
    );

    // Surface minCharacters errors for too-short pre-populated values once the fields are mounted. Call from the
    // modal's afterOpenChange so the Form.Items exist (destroyOnClose).
    const seedOnOpen = (form: FormInstance<FixRequestFormValues>) => {
        const fieldErrors = formFieldsSettings
            .filter((field) => {
                if (!field.minCharacters || field.minCharacters <= 0) return false;
                const val = field.prePopulatedText;
                return typeof val === 'string' && val.length > 0 && runeCount(val) < field.minCharacters;
            })
            .map((field) => ({
                name: `customField_${getFixRequestFormFieldId(field)}`,
                errors: [
                    `${field.name} is too short (${runeCount(field.prePopulatedText ?? '')}/${field.minCharacters})`,
                ],
            }));
        if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
        }
    };

    const buildFormFieldsValues = (values: FixRequestFormValues): FixRequestFormFieldValue[] =>
        formFieldsSettings.map((field) => ({
            fieldId: getFixRequestFormFieldId(field),
            value: values[`customField_${getFixRequestFormFieldId(field)}`] ?? '',
        }));

    return { nextVersion, fields, seedOnOpen, buildFormFieldsValues };
};
